import React, { useMemo } from 'react';
import { RotateCcwIcon } from 'lucide-react';
import type { SourceFilter } from '../../types/dashboard';
import { useFilters } from '../../contexts/FilterContext';
import { territories } from '../../data/territories';
import { PROGRAM_TYPES, PROGRAM_YEARS, SEASONS, SPORTS } from '../../data/dimensions';
import { coachMatches, resolveTerritoryIds } from '../../utils/filters';
import { resolveRange } from '../../utils/calendar';
import { AD_NAMES, FBC_NAMES } from '../../utils/territory';
import { SOURCE_META, sourceLabel } from '../../utils/theme';
import { FilterDropdown, type DropdownOption } from '../ui/FilterDropdown';
import { InfoTip } from '../ui/InfoTip';
import { DateRangeDropdown } from './DateRangeDropdown';
import { useMetricParam } from '../../hooks/useMetricParam';
import { metricMeta } from '../../utils/metricConfig';
export type FilterMode = 'full' | 'retention' | 'entity';
const toOptions = (list: string[]): DropdownOption[] => list.map(v => ({
  value: v,
  label: v
}));
export function FilterBar({
  mode
}: {
  mode: FilterMode;
}) {
  const {
    filters,
    update,
    reset,
    isDefault
  } = useFilters();
  const [metric] = useMetricParam();
  const retention = mode === 'retention';
  const entity = mode === 'entity';
  /** New-member counts have no program attribution, so program filters cannot apply to them. */
  const memberMetric = metric === 'members';
  const programReason = 'Not applied to New Members: a member is only linked to a program through a qualifying registration. Applies to Registrations, Conversion Rate, Revenue, Registrations per Member and Retention.';
  const territoryOptions = useMemo(() => territories.filter(t => coachMatches(t, filters.coach)).map(t => ({
    value: t.id,
    label: `${t.name}, ${t.state}`
  })), [filters.coach]);
  const territoryIds = useMemo(() => resolveTerritoryIds(filters), [filters]);
  const aopOptions = useMemo(() => territories.filter(t => territoryIds.includes(t.id)).flatMap(t => t.aops.map(a => ({
    value: a.id,
    label: a.name,
    group: t.name
  }))), [territoryIds]);
  const coachOptions: DropdownOption[] = [...FBC_NAMES.map(n => ({
    value: `fbc:${n}`,
    label: n,
    group: 'Franchise Business Coach'
  })), ...AD_NAMES.map(n => ({
    value: `ad:${n}`,
    label: n,
    group: 'Area Developer'
  }))];
  const range = resolveRange(filters);
  const entityReason = 'Set by the selected territory / AOP';
  const retentionReason = 'Retention uses season cohort selectors, not the acquisition date range';
  const scopeText = `${territoryIds.length} of ${territories.length} territories · ${filters.aops.length ? `${filters.aops.length} AOPs` : 'All AOPs'}`;
  const dateText = memberMetric ? `Acquisition date ${range.label}` : `Acquisition & registration date ${range.label}`;
  const sourceText = filters.source === 'All' ? 'All sources' : `${sourceLabel(filters.source)} only`;
  const context = retention ? `Program season cohorts · ${scopeText} · date range & source not applied` : entity ? `${dateText} · territory & AOP set by drilldown` : `${dateText} · ${scopeText} · ${sourceText} · Oct ’26 month-to-date excluded`;
  return <div className="sticky top-0 z-30 border-b border-line bg-white px-4 py-2.5 md:px-6" data-id="element-237" data-mp="1j3mr4t4gdx16">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-2" data-id="element-238" data-mp="1o0wdfzwjpsks">
        <div className="flex flex-wrap items-center gap-2" data-id="element-239" data-mp="2br8sduq7kpkv">
          <span className="w-[128px] shrink-0 text-xs font-semibold text-navy" data-id="element-240" data-mp="2brsapcjrtfzf">Date & scope</span>
          <DateRangeDropdown disabled={retention} disabledReason={retentionReason} data-id="element-241" data-mp="2fsc9slnwmto7" />
          <InfoTip align="left" width={340} content={<>
                <b data-id="element-243" data-mp="26yz5yooay7nc">Three different date bases are used:</b>
                <br data-id="element-244" data-mp="2upl49o8i8dk5" />
                <b data-id="element-245" data-mp="2up380rludzc8">1. Acquisition date</b> (date range): selects the member cohort — the date a member was first associated with the franchise. Field pending validation.
                <br data-id="element-246" data-mp="1aus4vbnam7iy" />
                <b data-id="element-247" data-mp="1ava50jd2t5w5">2. Registration / transaction date</b> (same date range): registration metrics count only cohort members’ registrations dated inside the range. Revenue date basis pending validation.
                <br data-id="element-248" data-mp="11vdcu7rh9owc" />
                <b data-id="element-249" data-mp="11uvixhsafmrl">3. Program season & year</b> (program filters and retention): the season of the registered program, never the purchase date.
              </>} data-id="element-242" data-mp="220nxah9qu36p" />
          <FilterDropdown label="Territory" options={territoryOptions} value={filters.territories} onChange={v => update({
          territories: v
        })} multiple searchable allLabel="All territories" disabled={entity} disabledReason={entityReason} menuWidth={260} data-id="element-250" data-mp="1tkgi9qv3p5ak" />
          <FilterDropdown label="AOP" options={aopOptions} value={filters.aops} onChange={v => update({
          aops: v
        })} multiple searchable allLabel="All AOPs" disabled={entity} disabledReason={entityReason} menuWidth={260} data-id="element-251" data-mp="2d9qw0zbadg61" />
          <FilterDropdown label="FBC / AD" options={coachOptions} value={filters.coach === 'all' ? [] : [filters.coach]} onChange={v => update({
          coach: v[0] ?? 'all'
        })} allLabel="All" disabled={entity} disabledReason={entityReason} menuWidth={240} data-id="element-252" data-mp="2kuutgfqd1n3e" />
          <FilterDropdown label="Source" options={SOURCE_META.map(s => ({
          value: s.key,
          label: s.label
        }))} value={filters.source === 'All' ? [] : [filters.source]} onChange={v => update({
          source: (v[0] ?? 'All') as SourceFilter
        })} allLabel="All" disabled={retention} disabledReason="Acquisition source does not apply to participant retention" menuWidth={180} data-id="element-253" data-mp="33qkx07dxyrje" />
          <button type="button" onClick={reset} disabled={isDefault} className="ml-auto inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-semibold text-accent-strong transition-colors duration-150 hover:bg-accent-soft disabled:cursor-default disabled:text-ink-subtle disabled:hover:bg-transparent" data-id="element-254" data-mp="1mfmbyvd68rpp">
            <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-255" data-mp="263lmc53tpwul" />
            Reset filters
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-256" data-mp="215tiqdttgmak">
          <span className="flex w-[128px] shrink-0 items-center gap-1 text-xs font-semibold text-navy" data-id="element-257" data-mp="217cz04fh2xcc">
            Program filters
            <InfoTip align="left" width={330} content={<>
                  <b data-id="element-259" data-mp="krjudjpkqxjq">Apply to member reporting:</b> acquisition date range, territory, AOP, FBC / AD and source.
                  <br data-id="element-260" data-mp="ft8k57vdal0z" />
                  <b data-id="element-261" data-mp="fts0ik5dd1cw">Apply to registration-derived measures only:</b> sport, program type, program season and program year narrow registrations, revenue, converted members and retention participants. Season and year come from the registered program, not the purchase date.
                  <br data-id="element-262" data-mp="2im7i6hzah0lp" />
                  {memberMetric ? 'They are disabled on New Members because no attribution rule links a member to a program before they register.' : retention ? 'On Retention, program season and year are chosen in the cohort controls instead.' : `Currently applied to ${metricMeta(metric).label}.`}
                </>} data-id="element-258" data-mp="2a56x9kwss125" />
          </span>
          <FilterDropdown label="Sport" options={toOptions(SPORTS)} value={filters.sports} onChange={v => update({
          sports: v
        })} multiple disabled={memberMetric} disabledReason={programReason} data-id="element-263" data-mp="23owxci0raidb" />
          <FilterDropdown label="Program type" options={toOptions(PROGRAM_TYPES)} value={filters.programs} onChange={v => update({
          programs: v
        })} multiple disabled={memberMetric} disabledReason={programReason} data-id="element-264" data-mp="2ltuknu4bikak" />
          <FilterDropdown label="Program season" options={toOptions(SEASONS)} value={filters.seasons} onChange={v => update({
          seasons: v
        })} multiple disabled={retention || memberMetric} disabledReason={memberMetric ? programReason : 'Retention selects seasons in its own cohort controls'} data-id="element-265" data-mp="275rdihejh2k6" />
          <FilterDropdown label="Program year" options={toOptions(PROGRAM_YEARS)} value={filters.years} onChange={v => update({
          years: v
        })} multiple disabled={retention || memberMetric} disabledReason={memberMetric ? programReason : 'Retention selects seasons in its own cohort controls'} menuWidth={180} data-id="element-266" data-mp="1o6lw1q90sv4c" />
          <p className="ml-auto text-[11.5px] text-ink-muted" data-id="element-267" data-mp="3v27be7bzld80">{context}</p>
        </div>
      </div>
    </div>;
}